import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  fetchLicenseHistory,
  LICENSE_HISTORY_PAGE_SIZE,
  type LicenseHistoryFilters,
} from "../../../api/licenseHistory";
import {
  ENUM_LICENSE_ACTION,
  enumlicenseactionToI18nKey,
  type EnumLicenseAction,
} from "../../../generated/enums";
import type { LicenseHistoryRow } from "../../../generated/types";
import { sectionToIcon } from "../../type/default";
import { LicenseActionBadge } from "./LicenseActionBadge";
import {
  ActiveFilterChips,
  type ActiveFilterChip,
} from "../../common/ActiveFilterChips";
import {
  DataTable,
  DataTableColumn,
  DataTableRow,
  DataTableSkeletonRows,
} from "../../common/DataTable";
import { EmptyState } from "../../common/EmptyState";
import {
  FILTER_BUTTON_PRIMARY,
  FILTER_BUTTON_SECONDARY,
  FILTER_CONTROL,
  FILTER_LABEL,
  FilterSelect,
} from "../../common/FilterControls";
import { EntityCard, EntityCardSkeletons } from "../../common/EntityCard";
import { FilterableValue } from "../../common/FilterableValue";
import { Pagination } from "../../common/Pagination";
import { HistorySearchInput } from "../../common/HistorySearchInput";
import {
  isKnownLicense,
  LicenseCodeList,
  licenseToI18nKey,
} from "../device/LicenseBadge";
import { LICENSE_OPTIONS } from "../../../api/license";

const EMPTY_FILTERS: LicenseHistoryFilters = {
  q: "",
  uuid: "",
  action: "",
  code: "",
  idAccount: undefined,
  idCompanyStock: undefined,
  idCompanyDevice: undefined,
  deviceName: "",
  dateFrom: "",
  dateTo: "",
};

/** Date + heure : sans l'heure, on ne distingue pas deux actions du même jour. */
const formatDate = (iso: string) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "-" : d.toLocaleString();
};

/** Repli « — » plutôt qu'une chaîne vide : une cellule vide se lit comme un bug. */
const orDash = (value: string) => (value.trim() === "" ? "-" : value);

/**
 * Onglet « Licences » de l'écran Historique (admin/PSC) : qui a attribué ou retiré
 * quelles licences, sur quel produit, et à quel stock.
 *
 * La particularité de cet onglet est la colonne **Stock débité** : quand elle diffère de
 * l'entreprise du produit, l'attribution a été faite par un admin au nom d'un
 * installateur partenaire — le cas que toute cette fonctionnalité existe pour rendre
 * visible. L'écart est signalé explicitement plutôt que laissé à la comparaison de deux
 * colonnes.
 *
 * Pas de `Header` : il est rendu une seule fois par HistoryPage, au-dessus des onglets.
 */
export const LicenseHistoryTable = () => {
  const { t } = useTranslation();

  // `applied` = ce qui part au serveur ; `draft` = ce que l'utilisateur saisit.
  // Les séparer évite une requête à chaque frappe.
  const [draft, setDraft] = useState<LicenseHistoryFilters>(EMPTY_FILTERS);
  const [applied, setApplied] = useState<LicenseHistoryFilters>(EMPTY_FILTERS);
  const [page, setPage] = useState(0);
  const [rows, setRows] = useState<LicenseHistoryRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const { page: result, error: err } = await fetchLicenseHistory(
      applied,
      page
    );
    setRows(result.rows);
    setTotal(result.total);
    setError(err);
    setLoading(false);
  }, [applied, page]);

  useEffect(() => {
    load();
  }, [load]);

  const activeFilterCount = useMemo(
    () =>
      Object.values(applied).filter((v) => v !== "" && v !== undefined).length,
    [applied]
  );

  /** Filtre posé par un CLIC : applique tout de suite, `draft` synchronisé. */
  const filterBy = (patch: Partial<LicenseHistoryFilters>) => {
    setPage(0);
    setDraft((prev) => ({ ...prev, ...patch }));
    setApplied((prev) => ({ ...prev, ...patch }));
  };

  /** Clic sur une date = le JOUR entier. */
  const filterByDay = (iso: string) => {
    const day = iso.slice(0, 10);
    filterBy({ dateFrom: day, dateTo: day });
  };

  /** Maj+clic : étend la plage en gardant la borne la plus ancienne déjà posée. */
  const extendToDay = (iso: string) => {
    const day = iso.slice(0, 10);
    const start = applied.dateFrom || day;
    const [from, to] = day < start ? [day, start] : [start, day];
    filterBy({ dateFrom: from, dateTo: to });
  };

  /** Puces des filtres actifs : le LIBELLÉ, jamais l'id envoyé au serveur. */
  const chips: ActiveFilterChip[] = [];
  if (applied.q) {
    chips.push({
      key: "q",
      field: t("filter-search"),
      value: applied.q,
      onRemove: () => filterBy({ q: "" }),
    });
  }
  if (applied.idAccount) {
    const label = rows.find(
      (r) => r.id_account === applied.idAccount
    )?.account_name;
    chips.push({
      key: "id_account",
      field: t("history-account"),
      value: label || `#${applied.idAccount}`,
      onRemove: () => filterBy({ idAccount: undefined }),
    });
  }
  if (applied.idCompanyStock) {
    const label = rows.find(
      (r) => r.id_company_stock === applied.idCompanyStock
    )?.company_stock_name;
    chips.push({
      key: "id_company_stock",
      field: t("history-stock"),
      value: label || `#${applied.idCompanyStock}`,
      onRemove: () => filterBy({ idCompanyStock: undefined }),
    });
  }
  if (applied.idCompanyDevice) {
    const label = rows.find(
      (r) => r.id_company_device === applied.idCompanyDevice
    )?.company_device_name;
    chips.push({
      key: "id_company_device",
      field: t("company"),
      value: label || `#${applied.idCompanyDevice}`,
      onRemove: () => filterBy({ idCompanyDevice: undefined }),
    });
  }
  if (applied.uuid) {
    chips.push({
      key: "uuid",
      field: t("device-uuid"),
      value: applied.uuid,
      onRemove: () => filterBy({ uuid: "" }),
    });
  }
  if (applied.deviceName) {
    chips.push({
      key: "device_name",
      field: t("history-device"),
      value: applied.deviceName,
      onRemove: () => filterBy({ deviceName: "" }),
    });
  }
  if (applied.action) {
    chips.push({
      key: "action",
      field: t("history-action"),
      value: String(t(enumlicenseactionToI18nKey[applied.action])),
      onRemove: () => filterBy({ action: "" }),
    });
  }
  if (applied.code) {
    chips.push({
      key: "code",
      field: t("history-code"),
      // Libellé traduit (« Pesage silo ») plutôt que le code brut (« pesage_silo »),
      // comme partout ailleurs sur l'écran. Code inconnu du catalogue : tel quel.
      value: isKnownLicense(applied.code)
        ? String(t(licenseToI18nKey[applied.code]))
        : applied.code,
      onRemove: () => filterBy({ code: "" }),
    });
  }
  if (applied.dateFrom || applied.dateTo) {
    const short = (d?: string) =>
      d ? d.split("-").reverse().slice(0, 2).join("/") : "…";
    const value =
      applied.dateFrom === applied.dateTo
        ? short(applied.dateFrom)
        : `${short(applied.dateFrom)} → ${short(applied.dateTo)}`;
    chips.push({
      key: "date",
      field: t("history-date"),
      value,
      onRemove: () => filterBy({ dateFrom: "", dateTo: "" }),
    });
  }

  const submitFilters = () => {
    setPage(0); // un nouveau filtre repart de la première page
    setApplied(draft);
  };

  const resetFilters = () => {
    setPage(0);
    setDraft(EMPTY_FILTERS);
    setApplied(EMPTY_FILTERS);
  };

  const scrollKey = `${page}|${JSON.stringify(applied)}`;

  const columns: DataTableColumn<
    "date" | "account" | "device" | "code" | "stock" | "action"
  >[] = [
    {
      key: "date",
      header: t("history-date"),
      className: "w-2/12 whitespace-nowrap",
    },
    { key: "account", header: t("history-account"), className: "w-2/12" },
    { key: "device", header: t("history-device"), className: "w-2/12" },
    { key: "code", header: t("history-code"), className: "w-2/12" },
    { key: "stock", header: t("history-stock"), className: "w-2/12" },
    { key: "action", header: t("history-action"), className: "w-2/12" },
  ];

  return (
    <div>
      {/* Barre de filtres — empilée en mobile, repliable sous `md`. */}
      <div className="mt-6">
        <button
          type="button"
          onClick={() => setFiltersOpen((v) => !v)}
          aria-expanded={filtersOpen}
          className="md:hidden flex items-center gap-2 text-default-text font-medium"
        >
          <FontAwesomeIcon icon="filter" />
          {t("filter-by")}
          {activeFilterCount > 0 && (
            <span className="rounded-full bg-secondary px-2 text-xs text-white">
              {activeFilterCount}
            </span>
          )}
        </button>

        <div
          className={`${filtersOpen ? "flex" : "hidden"} md:flex flex-col md:flex-row md:flex-wrap md:items-end gap-3 mt-3 md:mt-0`}
        >
          <HistorySearchInput
            label={t("filter-search")}
            value={draft.q ?? ""}
            onChange={(value) => setDraft({ ...draft, q: value })}
            onSubmit={submitFilters}
            // i18n.t("history-search-placeholder-license")
            placeholder={t("history-search-placeholder-license")}
          />

          <label className={FILTER_LABEL}>
            {t("history-action")}
            <FilterSelect
              value={draft.action ?? ""}
              onChange={(e) =>
                setDraft({
                  ...draft,
                  action: e.target.value as EnumLicenseAction | "",
                })
              }
            >
              <option value="">{t("history-all")}</option>
              {ENUM_LICENSE_ACTION.map((a) => (
                <option key={a} value={a}>
                  {t(enumlicenseactionToI18nKey[a])}
                </option>
              ))}
            </FilterSelect>
          </label>

          {/* Liste fermee plutot qu'un champ libre : les codes sont un catalogue fini, et
              une faute de frappe (« pesage silo », « PESAGE_SILO ») ne renvoyait aucun
              resultat sans dire pourquoi. Le back fait un ILIKE partiel sur la colonne
              « codes » (plusieurs codes separes par des virgules) : envoyer le code exact
              fonctionne donc tel quel, aucun changement back necessaire. */}
          <label className={FILTER_LABEL}>
            {t("history-code")}
            <FilterSelect
              value={draft.code ?? ""}
              onChange={(e) => setDraft({ ...draft, code: e.target.value })}
            >
              <option value="">{t("history-all")}</option>
              {LICENSE_OPTIONS.map((code) => (
                <option key={code} value={code}>
                  {t(licenseToI18nKey[code])}
                </option>
              ))}
            </FilterSelect>
          </label>

          <label className={FILTER_LABEL}>
            {t("history-date-from")}
            <input
              type="date"
              value={draft.dateFrom ?? ""}
              onChange={(e) => setDraft({ ...draft, dateFrom: e.target.value })}
              className={FILTER_CONTROL}
            />
          </label>

          <label className={FILTER_LABEL}>
            {t("history-date-to")}
            <input
              type="date"
              value={draft.dateTo ?? ""}
              onChange={(e) => setDraft({ ...draft, dateTo: e.target.value })}
              className={FILTER_CONTROL}
            />
          </label>

          <div className="flex gap-2">
            <button
              type="button"
              onClick={submitFilters}
              className={FILTER_BUTTON_PRIMARY}
            >
              {t("filter-search")}
            </button>
            <button
              type="button"
              onClick={resetFilters}
              className={FILTER_BUTTON_SECONDARY}
            >
              {t("history-reset")}
            </button>
          </div>
        </div>
      </div>

      {/* Au-dessus des résultats : un filtre qui ne renvoie rien doit rester visible
          et retirable. */}
      <ActiveFilterChips chips={chips} onClearAll={resetFilters} />

      {error ? (
        <p className="mt-10 text-center text-red-700">{t("error")}</p>
      ) : loading ? (
        <>
          <div className="md:hidden mt-6">
            <EntityCardSkeletons rows={4} />
          </div>
          <div className="hidden md:block mt-6">
            <DataTable columns={columns} scrollKey={scrollKey}>
              <DataTableSkeletonRows columns={columns} />
            </DataTable>
          </div>
        </>
      ) : rows.length === 0 ? (
        <EmptyState
          icon={sectionToIcon.section_history}
          title={
            activeFilterCount > 0
              ? t("table-no-result")
              : t("history-empty-license")
          }
        />
      ) : (
        <>
          {/* MOBILE : une carte par action, au style des cartes Produits, avec les mêmes
              filtres que le tableau. */}
          <div className="md:hidden space-y-4 mt-6">
            {rows.map((row) => (
              <EntityCard
                key={row.id}
                badge={
                  <FilterableValue
                    variant="badge"
                    label={<LicenseActionBadge action={row.action} />}
                    active={applied.action === row.action}
                    onFilter={() => filterBy({ action: row.action })}
                    title={t("filter-title-action")}
                  />
                }
                title={
                  row.account_name.trim() === "" ? (
                    "-"
                  ) : (
                    <FilterableValue
                      label={row.account_name}
                      active={applied.idAccount === row.id_account}
                      onFilter={() => filterBy({ idAccount: row.id_account })}
                      title={t("filter-title-user")}
                    />
                  )
                }
                rows={[
                  {
                    label: t("history-date"),
                    value: (
                      <FilterableValue
                        label={formatDate(row.date)}
                        active={
                          applied.dateFrom === row.date.slice(0, 10) &&
                          applied.dateTo === row.date.slice(0, 10)
                        }
                        onFilter={() => filterByDay(row.date)}
                        title={t("filter-title-day")}
                      />
                    ),
                  },
                  {
                    label: t("history-device"),
                    value:
                      row.device_name.trim() === "" ? (
                        "-"
                      ) : (
                        <FilterableValue
                          label={row.device_name}
                          active={applied.deviceName === row.device_name}
                          onFilter={() =>
                            filterBy({ deviceName: row.device_name })
                          }
                          title={t("filter-title-device")}
                        />
                      ),
                  },
                  {
                    label: t("history-code"),
                    value: (
                      <LicenseCodeList
                        codes={row.codes}
                        activeCode={applied.code}
                        onCodeClick={(code) => filterBy({ code })}
                      />
                    ),
                  },
                  {
                    label: t("history-stock"),
                    value:
                      row.company_stock_name.trim() === "" ? (
                        "-"
                      ) : (
                        <FilterableValue
                          label={row.company_stock_name}
                          active={
                            applied.idCompanyStock === row.id_company_stock
                          }
                          onFilter={() =>
                            filterBy({ idCompanyStock: row.id_company_stock })
                          }
                          title={t("filter-title-stock")}
                        />
                      ),
                  },
                ]}
              />
            ))}
          </div>

          {/* DESKTOP */}
          <div className="hidden md:block mt-6 overflow-x-auto">
            <DataTable columns={columns} scrollKey={scrollKey}>
              {rows.map((row) => {
                // Le stock débité diffère de l'entreprise du produit : c'est une
                // attribution faite par un admin AU NOM d'un installateur. On le dit
                // explicitement plutôt que de laisser comparer deux colonnes.
                const onBehalf = row.id_company_stock !== row.id_company_device;
                return (
                  <DataTableRow
                    key={row.id}
                    columns={columns}
                    cells={{
                      date: (
                        <>
                          {/* Clic = ce jour ; Maj+clic = étendre la plage. */}
                          <FilterableValue
                            label={formatDate(row.date)}
                            active={
                              applied.dateFrom === row.date.slice(0, 10) &&
                              applied.dateTo === row.date.slice(0, 10)
                            }
                            onFilter={() => filterByDay(row.date)}
                            onRangeSelect={() => extendToDay(row.date)}
                            title={t("filter-title-day-range")}
                          />
                        </>
                      ),
                      account: (
                        <>
                          <span className="text-default-text">
                            {row.account_name.trim() === "" ? (
                              "-"
                            ) : (
                              <FilterableValue
                                label={row.account_name}
                                active={applied.idAccount === row.id_account}
                                onFilter={() =>
                                  filterBy({ idAccount: row.id_account })
                                }
                                title={t("filter-title-user")}
                              />
                            )}
                          </span>
                          <span className="block text-xs text-grey-readable">
                            {row.account_type}
                          </span>
                        </>
                      ),
                      device: (
                        <>
                          <span className="text-default-text">
                            {row.device_name.trim() === "" ? (
                              "-"
                            ) : (
                              <FilterableValue
                                label={row.device_name}
                                active={applied.deviceName === row.device_name}
                                onFilter={() =>
                                  filterBy({ deviceName: row.device_name })
                                }
                                title={t("filter-title-device")}
                              />
                            )}
                          </span>
                          <span className="block text-xs text-grey-readable break-all">
                            <FilterableValue
                              label={row.uuid}
                              active={applied.uuid === row.uuid}
                              onFilter={() => filterBy({ uuid: row.uuid })}
                              title={t("filter-title-uuid")}
                            />
                          </span>
                        </>
                      ),
                      code: (
                        <LicenseCodeList
                          codes={row.codes}
                          activeCode={applied.code}
                          onCodeClick={(code) => filterBy({ code })}
                        />
                      ),
                      stock: (
                        <>
                          <span className="text-default-text">
                            {row.company_stock_name.trim() === "" ? (
                              "-"
                            ) : (
                              <FilterableValue
                                label={row.company_stock_name}
                                active={
                                  applied.idCompanyStock ===
                                  row.id_company_stock
                                }
                                onFilter={() =>
                                  filterBy({
                                    idCompanyStock: row.id_company_stock,
                                  })
                                }
                                title={t("filter-title-stock")}
                              />
                            )}
                          </span>
                          {onBehalf && (
                            <span className="block text-xs text-grey-readable">
                              {t("history-stock-on-behalf", {
                                company: orDash(row.company_device_name),
                              })}
                            </span>
                          )}
                        </>
                      ),
                      action: (
                        <FilterableValue
                          variant="badge"
                          label={<LicenseActionBadge action={row.action} />}
                          active={applied.action === row.action}
                          onFilter={() => filterBy({ action: row.action })}
                          title={t("filter-title-action")}
                        />
                      ),
                    }}
                  />
                );
              })}
            </DataTable>
          </div>

          <Pagination
            page={page}
            pageSize={LICENSE_HISTORY_PAGE_SIZE}
            total={total}
            onPageChange={setPage}
          />
        </>
      )}
    </div>
  );
};
